// @flow

import * as React from 'react';
import { Query } from 'react-apollo';
import { connect } from 'react-redux';

import Logging from '@ta/common.logging';
import { LoadingErrorMessage } from '@ta/loading.error';
import { LoadingSpinner } from '@ta/styleguide.loading';
import type { SuggestedFolloweesTypeInput } from '@ta/social.member-types';
import { RecommendedFolloweesImpressionIdContext } from '@ta/tracking.interactions';

import RecommendedFolloweesSectionsQuery from './RecommendedFolloweesSections.gql';
// eslint-disable-next-line import/no-cycle
import FollowShelf from './FollowShelf';
import SuggestedTripAdvisorMembersSingleSection from './SuggestedTripAdvisorMembersSingleSection';

import styles from './SuggestedTripAdvisorMembersSections.less';

type ExternalProps = {|
  /** a list of prioritized types to show at the top of the sections */
  prioritizedTypes?: SuggestedFolloweesTypeInput[],
  /** a max limit of items per shelf - default is 50 */
  limit?: number,
  /** location id to scope relevant suggestions */
  locationId?: number,
  /** An optional fallback component to display if there are no follow-by-interest shelves for this POS. */
  noShelvesFallback?: React.Node,
|};

type Props = {|
  ...ExternalProps,
  /** prop supplied from redux store via connect */
  userId: string,
  /** locale to filter relevant suggestions */
  locale: string,
|};

const SuggestedTripAdvisorMembersSections = (props: Props) => (
  <Query
    errorPolicy="all"
    query={RecommendedFolloweesSectionsQuery}
    variables={{
      locationId: props.locationId,
      locale: props.locale,
      /* $FlowFixGraphql (WP-1357) */
      prioritizedTypes: props.prioritizedTypes,
      limit: props.limit,
    }}
  >
    {({ data, error, loading, refetch }) => {
      if (loading) {
        return (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        );
      }

      if (error) {
        Logging.error(
          // $FlowFixGraphql
          `Error fetching SuggestedTripAdvisorMembersSections data for ${props.userId}: ${error}`,
        );

        // $FlowFixGraphql
        return <LoadingErrorMessage refetch={refetch} />;
      }

      if (data && data.recommendedFolloweesSections && data.recommendedFolloweesSections.sections) {
        // if sections.length is 1, use the original list view instead of individual follow shelves.
        if (data.recommendedFolloweesSections.sections.length === 1) {
          // However if this prop is specified, return whatever the fallback component is.
          if (props.noShelvesFallback) {
            return props.noShelvesFallback;
          }
          return (
            <RecommendedFolloweesImpressionIdContext.Provider
              /* $FlowFixGraphql (WP-1357) */
              value={{
                /* $FlowFixGraphql (WP-1357) */
                recommendedFolloweesImpressionId: data.recommendedFolloweesSections.impressionId,
              }}
              /* $FlowFixGraphql (WP-1357) */
            >
              <SuggestedTripAdvisorMembersSingleSection
                /* $FlowFixGraphql (WP-1357) */
                suggestedFollowees={data.recommendedFolloweesSections.sections[0].items.map(
                  item => ({
                    /* $FlowFixGraphql (WP-1357) */
                    userProfile: item.object,
                    /* $FlowFixGraphql (WP-1357) */
                    info: item.suggestedFolloweesInfo,
                  }),
                )}
              />
            </RecommendedFolloweesImpressionIdContext.Provider>
            /* $FlowFixGraphql (WP-1357) */
          );
        }
        return (
          <RecommendedFolloweesImpressionIdContext.Provider
            value={{
              /* $FlowFixGraphql (WP-1357) */
              recommendedFolloweesImpressionId: data.recommendedFolloweesSections.impressionId,
            }}
          >
            {/* $FlowFixGraphql (WP-1357) */}
            <div className={styles.sections}>
              {/* $FlowFixGraphql (WP-1357) */}
              {data.recommendedFolloweesSections.sections.map(section =>
                /* $FlowFixGraphql (WP-1357) */
                section.items && section.items.length > 2 ? (
                  <FollowShelf
                    /* $FlowFixGraphql (WP-1357) */
                    key={section.id}
                    /* $FlowFixGraphql (WP-1357) */
                    suggestedFollowees={section.items}
                    /* $FlowFixGraphql (WP-1357) */
                    shelfId={section.id}
                    /* $FlowFixGraphql (WP-1357) */
                    title={section.title}
                    hideShowMore
                    showBio
                    forceSimple
                  />
                ) : null,
              )}
            </div>
          </RecommendedFolloweesImpressionIdContext.Provider>
        );
      }

      return null;
    }}
  </Query>
);

SuggestedTripAdvisorMembersSections.defaultProps = {
  prioritizedTypes: [],
  limit: 20,
  noShelvesFallback: undefined,
};

export default connect<Props, ExternalProps, _, _, _, _>((state, ownProps: ExternalProps) => ({
  userId: state.auth.loggedInUserId,
  locationId: ownProps.locationId || state.page.geoId || null,
  locale: state.i18n.locale,
}))(SuggestedTripAdvisorMembersSections);
